import { computed, ref, type ComputedRef, type Ref } from 'vue'

import { insightActionsApi } from '../api/InsightActionsApi'
import type { OutcomeBucket } from '../components/ActionOutcomeSummary.vue'
import { ApprovalRunFlow } from '../flows/ApprovalRunFlow'
import type { BulkRejectResultRow } from '../models/insightAction.types'
import { itemIdsFor, type ApprovalRunGroup } from '../service/approvalRunGroups'

import { useInsightActionScope } from './useInsightActionScope'

import { useI18n } from '@/shared/i18n'

export type RunVerb = 'approve' | 'reject'

export interface RunOutcome {
  submitted: number
  buckets: OutcomeBucket[]
}

export interface UseApprovalRunDecision {
  asking: Ref<RunVerb | ''>
  busy: Ref<boolean>
  errorCode: Ref<string>
  selected: Ref<Set<string>>
  outcome: Ref<RunOutcome | null>
  /** A selection decides on itself; with none, the run's whole pending set is meant. */
  decidingCount: ComputedRef<number>
  toggle: (requestId: string) => void
  ask: (verb: RunVerb) => void
  keep: () => void
  /** Resolves `true` when the server took the decision. */
  decide: () => Promise<boolean>
}

/** Internal injection seam: production callers use the one-argument contract. */
export interface UseApprovalRunDecisionOptions {
  flow?: Pick<ApprovalRunFlow, 'approve' | 'reject'>
}

export function useApprovalRunDecision(
  group: () => ApprovalRunGroup,
  options: UseApprovalRunDecisionOptions = {},
): UseApprovalRunDecision {
  const { t } = useI18n()
  const scope = useInsightActionScope()
  const flow = options.flow ?? new ApprovalRunFlow({ api: insightActionsApi })

  const asking = ref<RunVerb | ''>('')
  const busy = ref(false)
  const errorCode = ref('')
  const selected = ref(new Set<string>())
  const outcome = ref<RunOutcome | null>(null)

  const decidingCount = computed(() => selected.value.size || group().pendingTotal)

  function toggle(requestId: string): void {
    const next = new Set(selected.value)
    if (!next.delete(requestId)) next.add(requestId)
    selected.value = next
  }

  function ask(verb: RunVerb): void {
    errorCode.value = ''
    outcome.value = null
    asking.value = verb
  }

  /** Approve is a queue receipt, not an outcome: say what was sent, not what landed. */
  function queued(count: number): RunOutcome {
    return {
      submitted: count,
      buckets: [
        { kind: 'waiting', title: t('insightActions.approvals.sentToPlatform'), count, rows: [] },
      ],
    }
  }

  function rejected(results: BulkRejectResultRow[]): RunOutcome {
    const refused = results.filter((row) => String(row.reasonCode) === 'rejected')
    const missed = results.length - refused.length
    return {
      submitted: results.length,
      buckets: [
        {
          kind: 'applied',
          title: t('insightActions.approvals.rejected'),
          count: refused.length,
          rows: [],
        },
        {
          kind: 'failed',
          title: t('insightActions.approvals.alreadyDecided'),
          count: missed,
          rows: [],
          message: missed ? t('insightActions.approvals.alreadyDecidedMessage') : '',
        },
      ],
    }
  }

  async function decide(): Promise<boolean> {
    const runScope = scope.forRun()
    const verb = asking.value
    if (!runScope || !verb || busy.value) return false

    busy.value = true
    try {
      const current = group()
      const itemIds = itemIdsFor(current, selected.value)
      if (verb === 'approve') {
        const result = await flow.approve(runScope, current.runId, itemIds)
        if (!result.ok) return fail(result.code)
        outcome.value = queued(result.value)
      } else {
        const result = await flow.reject(runScope, current.runId, itemIds)
        if (!result.ok) return fail(result.code)
        outcome.value = rejected(result.value)
      }
      asking.value = ''
      selected.value = new Set()
      return true
    } finally {
      busy.value = false
    }
  }

  function fail(code: string): false {
    errorCode.value = code || 'INSIGHTS_UNAVAILABLE'
    return false
  }

  return {
    asking,
    busy,
    errorCode,
    selected,
    outcome,
    decidingCount,
    toggle,
    ask,
    keep: () => {
      asking.value = ''
    },
    decide,
  }
}
